/**
 * Record a collection, and declare a cash handover.
 *
 * The control that matters: cash collected in the field is a **liability until
 * Admin confirms receipt**. The collector declares what they are handing over;
 * only Admin confirms the count. The person who collects can never be the
 * person who settles — that separation is enforced by the server, and this
 * screen makes it visible rather than hiding it.
 */

import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  Banner, Button, Card, Chip, Empty, Money, MoneyInput, PhotoTile, Select,
  Sheet, Skeleton, TextArea, TextInput, useToast,
} from '../../components/ui';
import { useQuery, useOnline, useRefreshOnFocus } from '../../lib/useApi';
import { useT } from '../../lib/i18n';
import { api, ApiError, uploadMedia } from '../../lib/api';
import * as outbox from '../../lib/outbox';
import { previewUrl, revokePreview, type CompressedImage } from '../../lib/camera';
import { ImageCaptureSheet } from '../../components/ImageCaptureSheet';
import { PaymentCard, derivePayment, type PaymentState } from '../../components/PaymentCard';
import { NoteCounter, noteTotalPaise, toDenominations, type NoteCounts } from '../../components/NoteCounter';
import { IconChevron } from '../../components/Icons';
import { displayPhone } from '../../lib/phone';
import { dateShort, daysBetween, money } from '../../lib/format';

interface Ledger {
  customer: { name: string; code: string; creditLimitPaise: number };
  outstandingPaise: number;
  invoices: {
    id: string; invoice_number: string; invoice_date: string; due_date: string | null;
    balance_amount_paise: number; payment_status: string; status: string;
  }[];
}

type Mode = 'cash' | 'upi';

// ---------------------------------------------------------------------------
// Record a collection against a customer
// ---------------------------------------------------------------------------

export function Collect() {
  const t = useT();
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useOnline();

  const state = (window.history.state?.usr ?? {}) as {
    shopId?: string; shopName?: string; outstandingPaise?: number;
  };

  const ledger = useQuery<Ledger>(customerId ? `/api/invoices/ledger/${customerId}` : null);

  const [amountPaise, setAmountPaise] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>('cash');
  const [reference, setReference] = useState('');
  const [photo, setPhoto] = useState<CompressedImage | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => () => { if (preview) revokePreview(preview); }, [preview]);

  const unpaid = useMemo(
    () => (ledger.data?.invoices ?? []).filter(
      (i) => i.status !== 'cancelled' && ['unpaid', 'part_paid', 'overdue'].includes(i.payment_status),
    ),
    [ledger.data],
  );

  /**
   * Payment proof, from the real camera.
   *
   * Same reason as the delivery photo: `<input capture>` is a hint an
   * installed PWA ignores, and a payment "proof" picked from the gallery
   * proves nothing about today.
   */
  function acceptPhoto(captured: CompressedImage) {
    if (preview) revokePreview(preview);
    setPhoto(captured);
    setPreview(previewUrl(captured.blob));
  }

  async function submit() {
    if (!amountPaise || amountPaise <= 0 || !customerId) return;
    setBusy(true);

    try {
      let proofMediaId: string | undefined;
      let localMediaId: string | undefined;
      if (photo) {
        if (online) {
          const uploaded = await uploadMedia({
            blob: photo.blob, purpose: 'upi_screenshot',
            originalSizeBytes: photo.originalSizeBytes,
            widthPx: photo.width, heightPx: photo.height,
          });
          proofMediaId = uploaded.mediaId;
        } else {
          localMediaId = await outbox.stashMedia(photo.blob, 'upi_screenshot');
        }
      }

      // Oldest invoice first, which is how a retailer thinks about clearing
      // dues and keeps the ageing report meaningful.
      const allocations: { invoiceId: string; amountPaise: number }[] = [];
      let remaining = amountPaise;
      for (const inv of unpaid) {
        if (remaining <= 0) break;
        const applied = Math.min(remaining, inv.balance_amount_paise);
        if (applied > 0) { allocations.push({ invoiceId: inv.id, amountPaise: applied }); remaining -= applied; }
      }

      const body = {
        customerId,
        customerShopId: state.shopId,
        amountPaise,
        paymentMode: mode,
        upiReference: mode === 'upi' ? reference.trim() || undefined : undefined,
        proofMediaId,
        allocations: allocations.length ? allocations : undefined,
      };

      if (!online) {
        await outbox.enqueue({
          method: 'POST', path: '/api/payments', body,
          label: `${t('collect.title')} — ${money(amountPaise)}`,
          kind: 'payment',
          mediaIds: localMediaId ? [localMediaId] : undefined,
        });
        toast.show(t('app.offlineHint'), 'ok');
        navigate(-1);
        return;
      }

      const result = await api.post<{
        receiptNumber: string; verificationStatus: string; requiresApproval: boolean; message: string;
      }>('/api/payments', body);

      toast.show(`${result.receiptNumber} · ${result.message}`, 'ok');
      navigate(-1);
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  const outstanding = ledger.data?.outstandingPaise ?? state.outstandingPaise ?? 0;

  return (
    <Page
      title={t('collect.title')}
      subtitle={state.shopName ?? ledger.data?.customer.name}
      back
    >
      <div className="stack">
        {ledger.loading && !ledger.data && <Skeleton rows={2} height={70} />}

        {outstanding > 0 && (
          <Card>
            <div className="row-between">
              <span className="t-sm t-muted">{t('ledger.outstanding')}</span>
              <Money paise={outstanding} className="t-lg" tone="danger" />
            </div>
          </Card>
        )}

        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <MoneyInput
              label={t('collect.amount')}
              required
              valuePaise={amountPaise}
              onChangePaise={setAmountPaise}
              hint={outstanding > 0 ? `${t('ledger.outstanding')}: ${money(outstanding)}` : undefined}
            />

            {/* Quick amounts — a salesperson taking ₹2,000 should not have to
                type it while standing at a counter. */}
            {outstanding > 0 && (
              <div className="row-wrap" style={{ gap: 8 }}>
                {[outstanding, 500_00, 1000_00, 2000_00, 5000_00]
                  .filter((v, i, arr) => v <= outstanding && arr.indexOf(v) === i)
                  .slice(0, 5)
                  .map((v) => (
                    <button
                      key={v} className="chip" style={{ padding: '8px 12px', cursor: 'pointer', border: 'none' }}
                      onClick={() => setAmountPaise(v)}
                    >{money(v, { compact: true })}</button>
                  ))}
              </div>
            )}

            <Select
              label={t('collect.mode')}
              required
              value={mode}
              options={[
                { value: 'cash', label: t('pay.cash') },
                { value: 'upi', label: t('pay.upi_online') },
              ]}
              onChange={(e) => setMode(e.target.value as Mode)}
            />

            {mode === 'upi' && (
              <TextInput
                label={t('ledger.utr')}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="12 digit reference"
              />
            )}

            <PhotoTile
              label={t('collect.proof')}
              previewUrl={preview}
              onCapture={() => setCameraOpen(true)}
              onRemove={() => { if (preview) revokePreview(preview); setPhoto(null); setPreview(null); }}
            />
          </div>
        </Card>

        {/* Cash credits immediately because the salesperson physically holds
            it; a UPI claim waits for Admin to check the bank. Saying which is
            which prevents an argument later. */}
        <Banner tone={mode === 'cash' ? 'warn' : 'info'}>
          <span className="t-sm">
            {mode === 'cash'
              ? 'Cash you collect stays against your name until admin confirms the handover.'
              : 'UPI payments are verified by admin before the balance updates.'}
          </span>
        </Banner>

        {unpaid.length > 0 && (
          <Card flush>
            <div className="card__header"><h3>{t('collect.against')}</h3></div>
            <div className="list">
              {unpaid.slice(0, 8).map((i) => {
                const overdue = i.due_date ? daysBetween(i.due_date) : 0;
                return (
                  <div key={i.id} className="list__item list__item--static">
                    <div className="grow">
                      <div className="row" style={{ gap: 8 }}>
                        <span className="t-sm t-semi">{i.invoice_number}</span>
                        {overdue > 0 && <Chip tone="danger">{overdue}d</Chip>}
                      </div>
                      <div className="t-xs t-muted">{dateShort(i.invoice_date)}</div>
                    </div>
                    <Money paise={i.balance_amount_paise} className="t-sm" />
                  </div>
                );
              })}
            </div>
            <div className="card__body">
              <span className="t-xs t-muted">
                Applied to the oldest invoice first
              </span>
            </div>
          </Card>
        )}
      </div>

      <div className="actionbar">
        <Button
          block size="lg" busy={busy}
          disabled={!amountPaise || amountPaise <= 0}
          onClick={() => void submit()}
        >{t('app.save')}</Button>
      </div>
      <ImageCaptureSheet
        open={cameraOpen}
        facing="environment"
        title="Attach the UPI confirmation"
        compressOptions={{ maxDimension: 1400, maxBytes: 2_000_000 }}
        onCapture={acceptPhoto}
        onClose={() => setCameraOpen(false)}
      />
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Cash in hand + declare handover
// ---------------------------------------------------------------------------

interface CashInHand {
  amountPaise: number;
  display: string;
  count: number;
  oldestCollectionDate: string | null;
  payments: {
    id: string; receipt_number: string; payment_date: string;
    amount_paise: number; customer_name: string; shop_name: string | null;
  }[];
  note: string;
}

/** An outstanding customer in the collection picker, from the customers list. */
interface OutCust {
  id: string; customer_code: string; legal_name: string; trade_name: string | null;
  store_type_name: string | null; phone: string; outstanding_paise: number; beat_name: string | null;
}

/**
 * Collect Cash / UPI.
 *
 * One screen for the whole job: find an outstanding shop, see its statement,
 * tick the invoices to settle, take the money on the approved payment card, and
 * (separately) hand the day's cash over. The payment card has no mode dropdown —
 * UPI and cash are entered side by side — so a part-cash, part-UPI payment is
 * recorded as two receipts, cash first against the oldest invoices, then UPI
 * against what is left. Any overpayment lands on account as an advance.
 */
export function Collections() {
  const t = useT();
  const toast = useToast();
  const online = useOnline();

  // --- Cash in hand + hand over (unchanged behaviour) ----------------------
  const cash = useQuery<CashInHand>('/api/payments/cash-in-hand/mine');
  const [handOverOpen, setHandOverOpen] = useState(false);
  // The handover is counted note by note; the tally IS the declared amount.
  const [notes, setNotes] = useState<NoteCounts>({});
  const declared = noteTotalPaise(notes);
  const [remarks, setRemarks] = useState('');
  const [hoBusy, setHoBusy] = useState(false);
  const handoverUuid = useRef<string | null>(null);
  // Start each handover from an empty counter, so the collector counts the
  // notes he actually has rather than confirming a pre-filled figure.
  useEffect(() => { if (handOverOpen) { setNotes({}); setRemarks(''); } }, [handOverOpen]);

  // --- Collect flow --------------------------------------------------------
  const [search, setSearch] = useState('');
  // The default list is the WHOLE-BOOK outstanding set, filtered server-side —
  // not page one of every customer filtered in the browser, which showed only
  // the outstanding shops that happened to be in the first hundred loaded (the
  // admin screen counted two, this one counted one). A search drops the filter
  // so the collector can still find any shop to take a payment from.
  const custs = useQuery<{ items: OutCust[] }>(
    search.trim()
      ? `/api/customers?pageSize=100&search=${encodeURIComponent(search.trim())}`
      : '/api/customers?pageSize=100&filter=outstanding',
    { deps: [search] },
  );
  // Today's collections, so a shop already paid today drops off the list even
  // when the payment is a UPI claim that has not yet reduced the invoice.
  const collQ = useQuery<{ items: { customerId: string; pendingUpiPaise: number }[] }>(
    '/api/payments/collections/mine',
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const ledger = useQuery<Ledger>(selectedId ? `/api/invoices/ledger/${selectedId}` : null, { deps: [selectedId] });
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [pay, setPay] = useState<PaymentState>({ discountPaise: 0, discountReason: '', cashPaise: 0, upiPaise: 0 });
  const [note, setNote] = useState('');
  // UPI is proven with a photograph of the confirmation, not a typed reference.
  const [upiPhoto, setUpiPhoto] = useState<CompressedImage | null>(null);
  const [upiPreview, setUpiPreview] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  function acceptUpiPhoto(img: CompressedImage) {
    if (upiPreview) revokePreview(upiPreview);
    setUpiPhoto(img);
    setUpiPreview(previewUrl(img.blob));
  }
  function clearUpiPhoto() {
    if (upiPreview) revokePreview(upiPreview);
    setUpiPhoto(null);
    setUpiPreview(null);
  }
  useEffect(() => () => { if (upiPreview) revokePreview(upiPreview); }, [upiPreview]);

  useRefreshOnFocus(() => { void cash.refetch(); void custs.refetch(); void collQ.refetch(); });

  const unpaid = useMemo(
    () => (ledger.data?.invoices ?? []).filter(
      (i) => i.status !== 'cancelled' && ['unpaid', 'part_paid', 'overdue'].includes(i.payment_status),
    ),
    [ledger.data],
  );

  // When a customer's ledger loads, tick every open invoice and clear the form.
  useEffect(() => {
    if (!ledger.data) return;
    const next: Record<string, boolean> = {};
    ledger.data.invoices
      .filter((i) => i.status !== 'cancelled' && ['unpaid', 'part_paid', 'overdue'].includes(i.payment_status))
      .forEach((i) => { next[i.id] = true; });
    setChecked(next);
    setPay({ discountPaise: 0, discountReason: '', cashPaise: 0, upiPaise: 0 });
    setNote(''); setUpiPhoto(null); setUpiPreview(null);
  }, [ledger.data]);

  // Net off today's pending UPI so a shop just collected from (whose invoice
  // balance has not yet moved) does not reappear as still owing.
  const pendingMap = new Map((collQ.data?.items ?? []).map((r) => [r.customerId, r.pendingUpiPaise]));
  const outRows = (custs.data?.items ?? [])
    .map((c) => ({ ...c, effectivePaise: Math.max(0, c.outstanding_paise - (pendingMap.get(c.id) ?? 0)) }))
    .filter((c) => c.effectivePaise > 0);
  const due = unpaid.filter((i) => checked[i.id]).reduce((s, i) => s + i.balance_amount_paise, 0);
  const d = derivePayment(due, pay);
  const accountOut = ledger.data?.outstandingPaise ?? 0;
  const outAfter = Math.max(0, accountOut - d.receivedPaise);

  async function declareHandover() {
    if (declared <= 0) {
      toast.show('Count the notes you are handing over.', 'danger');
      return;
    }
    // One idempotency key per handover (item #3): reused on a retry, cleared on
    // success, so a double-tap or a resend declares once.
    if (!handoverUuid.current) handoverUuid.current = crypto.randomUUID();
    // A short handover must carry a reason — it is what the admin sees when
    // confirming, and what explains the balance that stays pending.
    const isShort = cash.data ? declared < cash.data.amountPaise : false;
    if (isShort && !remarks.trim()) {
      toast.show('Add a reason — you are handing over less than you are holding.', 'danger');
      return;
    }
    setHoBusy(true);
    try {
      const result = await api.post<{
        settlementId: string; expectedAmountPaise: number; variancePaise: number; note: string;
      }>('/api/payments/settlements', {
        declaredAmountPaise: declared,
        remarks: remarks.trim() || undefined,
        denominations: toDenominations(notes),
        clientUuid: handoverUuid.current,
      });
      handoverUuid.current = null;
      toast.show(result.note ?? 'Handover already declared.', 'ok');
      setHandOverOpen(false);
      void cash.refetch();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setHoBusy(false);
    }
  }

  async function record() {
    if (!selectedId || d.receivedPaise <= 0) return;
    if (!online) { toast.show('Connect to the internet to record a collection.', 'danger'); return; }
    if (pay.upiPaise > 0 && !upiPhoto) {
      toast.show('Photograph the UPI confirmation for the UPI amount.', 'danger'); return;
    }
    setBusy(true);
    try {
      // The UPI confirmation photo proves the claim admin will verify.
      let proofMediaId: string | undefined;
      if (pay.upiPaise > 0 && upiPhoto) {
        const up = await uploadMedia({
          blob: upiPhoto.blob, purpose: 'upi_screenshot',
          originalSizeBytes: upiPhoto.originalSizeBytes,
          widthPx: upiPhoto.width, heightPx: upiPhoto.height,
        });
        proofMediaId = up.mediaId;
      }
      // Allocate oldest-first: cash takes the oldest invoices, then UPI takes
      // what is left. The server checks each receipt's allocations against the
      // balances AT THE TIME IT IS POSTED, so cash must be posted before UPI.
      const picked = unpaid.filter((i) => checked[i.id]).slice()
        .sort((a, b) => a.invoice_date.localeCompare(b.invoice_date));
      const bals = new Map(picked.map((i) => [i.id, i.balance_amount_paise]));
      const alloc = (amount: number) => {
        const out: { invoiceId: string; amountPaise: number }[] = [];
        let rem = amount;
        for (const inv of picked) {
          if (rem <= 0) break;
          const b = bals.get(inv.id) ?? 0;
          const applied = Math.min(rem, b);
          if (applied > 0) { out.push({ invoiceId: inv.id, amountPaise: applied }); bals.set(inv.id, b - applied); rem -= applied; }
        }
        return out;
      };
      const cashAllocs = pay.cashPaise > 0 ? alloc(pay.cashPaise) : [];
      const upiAllocs = pay.upiPaise > 0 ? alloc(pay.upiPaise) : [];

      const receipts: string[] = [];
      if (pay.cashPaise > 0) {
        const r = await api.post<{ receiptNumber: string; message: string }>('/api/payments', {
          customerId: selectedId, amountPaise: pay.cashPaise, paymentMode: 'cash',
          allocations: cashAllocs.length ? cashAllocs : undefined,
          remarks: note.trim() || undefined,
        });
        receipts.push(r.receiptNumber);
      }
      if (pay.upiPaise > 0) {
        const r = await api.post<{ receiptNumber: string; message: string }>('/api/payments', {
          customerId: selectedId, amountPaise: pay.upiPaise, paymentMode: 'upi',
          proofMediaId,
          allocations: upiAllocs.length ? upiAllocs : undefined,
          remarks: note.trim() || undefined,
        });
        receipts.push(r.receiptNumber);
      }
      toast.show(`Recorded ${money(d.receivedPaise)} · ${receipts.join(' + ')}`, 'ok');
      setSelectedId(null);
      void cash.refetch(); void custs.refetch(); void collQ.refetch();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  const ageDays = cash.data?.oldestCollectionDate ? daysBetween(cash.data.oldestCollectionDate) : 0;

  // The cash still held, broken DOWN BY DATE — so a driver or salesman who has
  // not deposited every day's takings can see which day's cash is still with
  // him. The total "Cash with you" is the sum across days; this says ₹200 is
  // from the 22nd and ₹5,819 from today, rather than one opaque figure.
  const cashByDate = (() => {
    const map = new Map<string, number>();
    for (const pmt of cash.data?.payments ?? []) {
      map.set(pmt.payment_date, (map.get(pmt.payment_date) ?? 0) + pmt.amount_paise);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, amount]) => ({ date, amount }));
  })();

  return (
    <Page
      title="Collect Cash / UPI"
      actions={(
        <Button
          size="sm" variant="ghost"
          onClick={() => { void cash.refetch(); void custs.refetch(); if (selectedId) void ledger.refetch(); }}
        >
          {t('app.refresh')}
        </Button>
      )}
    >
      <div className="stack">
        {/* Cash in hand — compact, taps through to hand over. */}
        {cash.data && cash.data.amountPaise > 0 && (
          <Card onClick={() => setHandOverOpen(true)}>
            <div className="row-between">
              <div style={{ minWidth: 0 }}>
                <div className="t-xs t-muted">{t('collect.cashInHand')}</div>
                <Money paise={cash.data.amountPaise} className="t-lg" />
                <div className="t-xs t-muted">
                  {cash.data.count} collection(s) · {ageDays > 2 ? `held ${ageDays}d · ` : ''}tap to hand over
                </div>
                {/* Date-wise breakdown of the held cash — shown when it spans
                    more than one day, so a part-deposited balance is clear. */}
                {cashByDate.length > 1 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 260 }}>
                    {cashByDate.map((d) => (
                      <div
                        key={d.date}
                        style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}
                      >
                        <span className="t-xs t-muted">{dateShort(d.date)}</span>
                        <span className="t-xs t-semi">{money(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <IconChevron width={20} height={20} style={{ color: 'var(--faint)' }} />
            </div>
          </Card>
        )}

        {/* A credit: the admin counted more than was due on a handover, so the
            admin owes the collector that much. It nets off the next handover. */}
        {cash.data && cash.data.amountPaise < 0 && (
          <Card>
            <div className="t-xs t-muted">Admin owes you</div>
            <Money paise={-cash.data.amountPaise} className="t-lg" tone="ok" />
            <div className="t-xs t-muted">Adjusts against your next handover.</div>
          </Card>
        )}

        {!selectedId ? (
          <>
            <TextInput
              placeholder="Search — store, code or phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search outstanding customers"
            />
            {custs.loading && !custs.data && <Skeleton rows={5} />}
            {custs.data && outRows.length === 0 && (
              <Empty title="No outstanding customers" hint="Shops that owe money will appear here" />
            )}
            {outRows.length > 0 && (
              <Card flush>
                <div className="card__header"><h3>Outstanding customers</h3></div>
                <div className="list">
                  {outRows.map((c) => (
                    <button key={c.id} className="list__item" onClick={() => setSelectedId(c.id)}>
                      <span className="grow" style={{ minWidth: 0, textAlign: 'left' }}>
                        <span className="t-sm t-semi t-clip" style={{ display: 'block' }}>{c.trade_name || c.legal_name}</span>
                        <span className="t-xs t-muted" style={{ display: 'block' }}>
                          {c.customer_code}{c.store_type_name ? ` · ${c.store_type_name}` : ''} · {displayPhone(c.phone)}
                        </span>
                      </span>
                      <Money paise={c.effectivePaise} tone="danger" compact className="t-semi" />
                      <IconChevron width={18} height={18} style={{ color: 'var(--faint)' }} />
                    </button>
                  ))}
                </div>
              </Card>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              style={{ alignSelf: 'flex-start', border: 0, background: 'transparent', color: 'var(--accent, #2b64f5)', cursor: 'pointer', font: 'inherit', fontWeight: 600, padding: '2px 0' }}
            >
              ← Choose another customer
            </button>

            {ledger.loading && !ledger.data && <Skeleton rows={3} height={70} />}

            {ledger.data && (
              <>
                {/* Statement of account */}
                <Card>
                  <div className="row-between">
                    <div style={{ minWidth: 0 }}>
                      <div className="t-semi t-clip">{ledger.data.customer.name}</div>
                      <div className="t-xs t-muted">{ledger.data.customer.code}</div>
                    </div>
                    <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                      <div className="t-xs t-muted">{t('ledger.outstanding')}</div>
                      <Money paise={ledger.data.outstandingPaise} className="t-lg" tone="danger" />
                    </div>
                  </div>
                </Card>

                {/* Pending invoices — tick to allocate */}
                {unpaid.length > 0 && (
                  <Card flush>
                    <div className="card__header"><h3>Pending invoices · tick to allocate</h3></div>
                    <div className="list">
                      {unpaid.map((i) => {
                        const overdue = i.due_date ? daysBetween(i.due_date) : 0;
                        return (
                          <label key={i.id} className="list__item" style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={!!checked[i.id]}
                              onChange={(e) => setChecked((prev) => ({ ...prev, [i.id]: e.target.checked }))}
                              style={{ width: 18, height: 18, accentColor: 'var(--brand)', marginRight: 10, flex: '0 0 auto' }}
                            />
                            <span className="grow" style={{ minWidth: 0 }}>
                              <span className="row" style={{ gap: 8 }}>
                                <span className="t-sm t-semi">{i.invoice_number}</span>
                                {overdue > 0 && <Chip tone="danger">{overdue}d</Chip>}
                              </span>
                              <span className="t-xs t-muted">{dateShort(i.invoice_date)}</span>
                            </span>
                            <Money paise={i.balance_amount_paise} className="t-sm" />
                          </label>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {/* The approved payment card — no door discount on a collection. */}
                <PaymentCard
                  totalPaise={due}
                  value={pay}
                  onChange={setPay}
                  showDiscount={false}
                  totalLabel="Due on selected invoices"
                />

                <Card>
                  <div className="stack-2">
                    <div className="row-between">
                      <span className="t-sm t-semi">Outstanding after this collection</span>
                      <Money paise={outAfter} className="t-sm" tone={outAfter > 0 ? 'danger' : undefined} />
                    </div>
                    {pay.upiPaise > 0 && (
                      <PhotoTile
                        label="Photograph the UPI confirmation"
                        previewUrl={upiPreview}
                        onCapture={() => setCameraOpen(true)}
                        onRemove={clearUpiPhoto}
                      />
                    )}
                    <TextArea
                      label="Why"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="What the customer said (optional)"
                      maxLength={500}
                    />
                  </div>
                </Card>

                <Banner tone={pay.cashPaise > 0 ? 'warn' : 'info'}>
                  <span className="t-sm">
                    {pay.cashPaise > 0
                      ? 'Cash you collect stays against your name until admin confirms the handover.'
                      : 'UPI payments are verified by admin before the balance updates.'}
                  </span>
                </Banner>
              </>
            )}

            <div className="actionbar">
              <Button
                block size="lg" busy={busy}
                disabled={d.receivedPaise <= 0}
                onClick={() => void record()}
              >
                {d.receivedPaise > 0 ? `${t('app.save')} · ${money(d.receivedPaise)}` : t('app.save')}
              </Button>
            </div>
          </>
        )}

        {!selectedId && cash.data && cash.data.amountPaise === 0 && outRows.length === 0 && !custs.loading && (
          <Empty title="Nothing to collect" hint="Outstanding customers will appear here" />
        )}
      </div>

      <Sheet
        open={handOverOpen}
        onClose={() => setHandOverOpen(false)}
        title={t('collect.handOver')}
        footer={
          <>
            <Button variant="ghost" block onClick={() => setHandOverOpen(false)} disabled={hoBusy}>
              {t('app.cancel')}
            </Button>
            <Button block busy={hoBusy} onClick={() => void declareHandover()}>
              {t('app.confirm')}
            </Button>
          </>
        }
      >
        <div className="stack-4">
          {/* Count the notes being handed over — the tally is the declared
              amount, and the breakdown reaches the admin when he confirms. */}
          <NoteCounter notes={notes} onChange={setNotes} title="Count the notes you are handing over" />

          <div className="row-between">
            <span className="t-semi">{t('collect.declaredAmount')}</span>
            <Money paise={declared} className="t-lg t-semi" />
          </div>
          {cash.data && (
            <div className="row-between">
              <span className="t-sm t-muted">System expects</span>
              <Money paise={cash.data.amountPaise} className="t-sm" />
            </div>
          )}

          {cash.data && declared > 0 && declared < cash.data.amountPaise && (
            <Banner tone="warn">
              <span className="t-sm">
                You are handing over {money(cash.data.amountPaise - declared)} less
                than you are holding. That balance stays pending against you until you hand it
                over — give the reason below.
              </span>
            </Banner>
          )}
          <TextArea
            label={cash.data && declared > 0 && declared < cash.data.amountPaise
              ? 'Reason for handing over less'
              : t('visit.notes')}
            required={cash.data ? declared > 0 && declared < cash.data.amountPaise : false}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            maxLength={500}
          />
          <Banner tone="info">
            <span className="t-xs">{t('collect.awaitingConfirm')} · {t('admin.cannotSettleOwn')}</span>
          </Banner>
        </div>
      </Sheet>

      <ImageCaptureSheet
        open={cameraOpen}
        facing="environment"
        title="Attach the UPI confirmation"
        compressOptions={{ maxDimension: 1400, maxBytes: 2_000_000 }}
        onCapture={(img) => { acceptUpiPhoto(img); setCameraOpen(false); }}
        onClose={() => setCameraOpen(false)}
      />
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Today's collections — the detail behind the dashboard "Collections" tile
// ---------------------------------------------------------------------------

interface CollectionsToday {
  date: string;
  cashPaise: number; upiPaise: number; totalPaise: number;
  cashDisplay: string; upiDisplay: string; totalDisplay: string;
  items: {
    customerId: string; customerName: string; customerCode: string;
    cashPaise: number; upiPaise: number; totalPaise: number;
    pendingUpiPaise: number; balancePaise: number;
    display: { cash: string; upi: string; total: string; balance: string };
  }[];
}

export function CollectionsTodayList() {
  const q = useQuery<CollectionsToday>('/api/payments/collections/mine');
  useRefreshOnFocus(q.refetch);

  return (
    <Page title="Today's collections" back>
      <div className="stack">
        {q.loading && !q.data && <Skeleton rows={4} height={70} />}
        {q.data && q.data.items.length === 0 && (
          <Empty title="No collections today" hint="Collections you record today appear here" />
        )}
        {q.data && q.data.items.length > 0 && (
          <>
            <Card>
              <div className="row-between">
                <div>
                  <div className="t-xs t-muted">Collected today</div>
                  <Money paise={q.data.totalPaise} className="t-2xl" />
                </div>
                <div className="row-wrap" style={{ gap: 6, justifyContent: 'flex-end' }}>
                  <Chip tone="ok">Cash {q.data.cashDisplay}</Chip>
                  <Chip tone="info">UPI {q.data.upiDisplay}</Chip>
                </div>
              </div>
            </Card>

            <Card flush>
              <div className="card__header"><h3>By customer</h3></div>
              <div className="list">
                {q.data.items.map((r) => (
                  <div key={r.customerId} className="list__item list__item--static" style={{ display: 'block' }}>
                    <div className="row-between">
                      <div style={{ minWidth: 0 }}>
                        <div className="t-sm t-semi t-clip">{r.customerName}</div>
                        <div className="t-xs t-muted">{r.customerCode}</div>
                      </div>
                      <Money paise={r.totalPaise} className="t-sm" />
                    </div>
                    <div className="row-wrap t-xs t-muted" style={{ gap: 12, marginTop: 5 }}>
                      <span>Cash <strong style={{ color: 'var(--ink)' }}>{r.display.cash}</strong></span>
                      <span>UPI <strong style={{ color: 'var(--ink)' }}>{r.display.upi}</strong></span>
                      <span>Balance <strong style={{ color: 'var(--ink)' }}>{r.display.balance}</strong></span>
                      {r.pendingUpiPaise > 0 && <Chip tone="warn">UPI pending</Chip>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </Page>
  );
}
